import type { ButtonProps as HeroButtonProps } from "@heroui/react";

export const COLORS = [
  "primary",
  "success",
  "warning",
  "danger",
  "default",
] as const;

export type ButtonVariant = "primary" | "secondary" | "tertiary" | "soft";
export type ButtonColor = (typeof COLORS)[number];

export const ICON_BUTTON_SIZE_CLASSES: Record<
  NonNullable<HeroButtonProps["size"]>,
  string
> = {
  sm: "!size-8", // 32px
  md: "!size-9", // 36px
  lg: "!size-10", // 40px
};

// Caret segment of a split button, and how far it tucks into the main segment.
// The box is floored at 24px even at sm because it is a touch target, so sm and
// md share a size and the scaling is carried by the chevron and the overlap.
export const SPLIT_CARET_CLASSES: Record<
  NonNullable<HeroButtonProps["size"]>,
  string
> = {
  sm: "size-6 -ml-1.5", // 24px, tucked 4px
  md: "size-6 -ml-1.5", // 24px, tucked 6px
  lg: "size-7 -ml-2", // 28px, tucked 8px
};

export const SPLIT_CARET_ICON_CLASS: Record<
  NonNullable<HeroButtonProps["size"]>,
  string
> = {
  sm: "!size-3", // 12px
  md: "!size-3.5", // 14px
  lg: "!size-4", // 16px
};

// v3's button CSS forces icon svgs to `sm:size-4` (16px); this restores the DS
// sizes (wins via layer order — utilities beat v3's components rule).
export const BUTTON_ICON_SIZE_CLASS: Record<
  NonNullable<HeroButtonProps["size"]>,
  string
> = {
  sm: "!size-4", // 16px
  md: "!size-4.5", // 18px
  lg: "!size-5", // 20px
};

export const VARIANT_MAP: Record<ButtonVariant, HeroButtonProps["variant"]> = {
  primary: "primary",
  secondary: "outline",
  tertiary: "ghost",
  soft: "secondary",
};

// Active deepens its own tint on hover rather than falling back to the neutral
// hover — losing the tint under the cursor would read as "deselected".
export const ACTIVE_STATE_CLASSES: Partial<Record<ButtonVariant, string>> = {
  secondary:
    "bg-accent-blue-soft hover:bg-accent-blue-soft-hover text-accent-blue-soft-foreground border-none",
  tertiary:
    "bg-accent-blue-soft hover:bg-accent-blue-soft-hover text-accent-blue-soft-foreground",
};
